import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import styles from '@Components/download-panel/download-panel.module.scss';
import {DownloadStateTypes, type OrderDownloadStateNew, removeOrder, setCurrentViewingOrderIdKey} from '@Components/download-panel/download-panel.reducer';
import {DownloadPanelUpselling} from '@Components/download-panel/components/download-panel-upselling/download-panel-upselling';
import {DownloadPanelPublishingSection} from '@Components/download-panel/components/download-panel-publishing-section/download-panel-publishing-section';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {noop} from '@Utils/general.util';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {abortOngoingDownload, MULTIPAGE_STATIC_FILE_DOWNLOAD_EXTENSION, shouldShowDownloadUpsellingForUser, triggerBrowserDownload} from '@Libraries/poster-download-library';
import {getAssetUrl} from '@Utils/s3.util';
import {DownloadYotpoSection} from '@Components/download-panel/components/download-yotpo-section/download-yotpo-section';
import {makeFilenameSafe} from '@Utils/file.util';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';

interface DownloadPanelDownloadingSectionProps {
  orderIdKey: string;
  onClickBack?: () => void;
  isOpenedInModal?: boolean;
  isOpenedInEditor?: boolean;
  isOpenedInPrintShop?: boolean;
  hasClosedUpsellingInThisSession?: boolean;
  onCloseUpselling?: () => void;
  isOpenedInLoadingPopup?: boolean;
}

interface SingleDownloadItemProps {
  order: OrderDownloadStateNew;
  showBottomPadding?: boolean;
  isOpenedInPrintShop: boolean;
  isLastVisible: boolean;
  onHide: (orderIdKey: string, isLastVisible: boolean) => void;
}

function SingleDownloadItem({order, isOpenedInPrintShop, showBottomPadding = true, isLastVisible, onHide}: SingleDownloadItemProps): ReactElement {
  const [isComplete, setIsComplete] = useState(order.state === DownloadStateTypes.READY);
  const [isError, setIsError] = useState(order.state === DownloadStateTypes.ERROR);
  const [imageLoadError, setImageLoadError] = useState(false);

  const name = order.name;
  const fileType = order.fileType;
  const displayProgress = order.displayProgress ?? 0;

  useEffect(() => {
    if (order.state === DownloadStateTypes.READY) {
      setIsComplete(true);
      setIsError(false);
    } else if (order.state === DownloadStateTypes.ERROR) {
      setIsError(true);
      setIsComplete(false);
    } else {
      setIsComplete(false);
      setIsError(false);
    }
  }, [order.state]);

  useEffect(() => {
    setImageLoadError(false);
  }, [order.thumbnailUrl]);

  const getFileExtensionForDownloads = (): string => {
    const isMultipageDownload = order.isMultipageDownload && order.selectedPages?.length && order.selectedPages.length > 1;

    if (!isMultipageDownload) {
      return fileType;
    }

    if (fileType === FileTypeOptions.MP4 || fileType === FileTypeOptions.GIF) {
      return fileType;
    }

    return MULTIPAGE_STATIC_FILE_DOWNLOAD_EXTENSION;
  };

  const roundedProgress = Math.round(displayProgress);
  const getExtendedOption = (): ReactElement | null => {
    const isReady = order.state === DownloadStateTypes.READY;
    const printShopLink = isReady ? order.downloadResult?.printShopLink : undefined;
    const downloadURL = isReady && order.state === DownloadStateTypes.READY ? order.downloadURL : undefined;

    const showDownloadLink = isReady && (printShopLink || downloadURL);
    const showDownloadAgainLink = (!printShopLink || isOpenedInPrintShop) && downloadURL;

    const handleDownloadClick = (): void => {
      if (showDownloadAgainLink) {
        const downloadSafePosterName = makeFilenameSafe(order.name, 'poster');
        triggerBrowserDownload(downloadURL, downloadSafePosterName, getFileExtensionForDownloads());
      } else if (printShopLink) {
        trackPosterBuilderGA4Events(GA4EventName.PRINT_SHOP_LINK);
        window.open(window.PMW.util.site_url(`cart/downloadOptions/${printShopLink}`), '_blank');
      }
    };

    return (
      <div className="spacing-m-t-1 flex-h-row flex-items-center flex-justify-between">
        <Text val={`${roundedProgress}%`} size={TextSize.XXSMALL} bold className={isError ? '_hidden' : isComplete ? 'content-success' : 'content-primary'} />
        {showDownloadLink ? (
          <Text
            onClick={handleDownloadClick}
            val={showDownloadAgainLink ? window.i18next.t('pmwjs_download_again') : window.i18next.t('pmwjs_share_download_link')}
            bold
            size={TextSize.XSMALL}
            className="content-primary _cursor-pointer"
            underline
          />
        ) : null}
      </div>
    );
  };

  const onHideNotificationForOrder = (): void => {
    onHide(order.orderIdKey, isLastVisible);
  };

  const getThumbnailContainer = (): ReactElement => {
    const showMultipageIndication = order.isMultipageDownload && order.selectedPages?.length && order.selectedPages.length > 1;

    return (
      <div className={showMultipageIndication ? styles.thumbnailWrapper : '_fit-content'}>
        <div
          className={`_position-relative ${styles.thumbnailContainer} ${showMultipageIndication ? styles.multipageContainer : ''}  ${imageLoadError || !order.thumbnailUrl ? styles.placeholderThumbnail : ''} spacing-m-r-3 radius-12 border-s-dark`}
        >
          <div
            onClick={onHideNotificationForOrder}
            className={`_position-absolute _cursor-pointer flex-center radius-round _fit-width _fit-height border-s-standard shadow-floating-elevation-2 ${styles.thumbnailHideIconContainer}`}
          >
            <Icon icon="icon-close" size={IconSize.SIZE_ICON_16} />
          </div>
          <img
            className={`${styles.thumbnail} _full-width _full-height`}
            src={imageLoadError || !order.thumbnailUrl ? getAssetUrl('placeholder-image.png') : order.thumbnailUrl}
            alt="img-thumbnail"
            onError={(): void => {
              setImageLoadError(true);
            }}
          />
          {showMultipageIndication ? (
            <>
              <div className={`_position-absolute ${styles.multipageIndicatorOne} radius-12 border-s-dark`} />
              <div className={`_position-absolute ${styles.multipageIndicatorTwo} radius-12 border-s-dark`} />
            </>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className={`flex flex-h-row flex-items-center content-body ${showBottomPadding ? 'spacing-m-b-4' : ''}`}>
      {getThumbnailContainer()}
      <div className={`flex-v-row ${styles.downloadInfoContainer}`}>
        <div className="flex-h-row spacing-m-b-1">
          <Text val={name} size={TextSize.XSMALL} bold className={styles.fileName} />
          <Text val={`.${getFileExtensionForDownloads()}`} size={TextSize.XSMALL} bold />
        </div>
        <div className={styles.progressBarContainer}>
          <span className={`${styles.progressBarLoader} ${isError ? styles.error : ''} ${isComplete ? styles.success : ''}`} style={{width: `${displayProgress}%`}} />
        </div>
        {getExtendedOption()}
      </div>
    </div>
  );
}

export function DownloadPanelDownloadingSection({
  orderIdKey,
  isOpenedInModal = false,
  isOpenedInPrintShop = false,
  isOpenedInEditor = true,
  hasClosedUpsellingInThisSession = false,
  onCloseUpselling = noop,
  isOpenedInLoadingPopup = false,
  ...props
}: DownloadPanelDownloadingSectionProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const orderStateHashmapNew = useAppSelector((state) => {
    return state.downloadDialog.orderStateHashmapNew;
  });
  const currentlyProcessingOrderIdKeys = useAppSelector((state) => {
    return state.downloadDialog.currentlyProcessingOrderIdKeys;
  });

  const ordersToDisplay = currentlyProcessingOrderIdKeys
    .map((key) => orderStateHashmapNew[key])
    .filter((order): order is OrderDownloadStateNew => order !== undefined && !order.isDropboxDownload)
    .reverse();

  if (ordersToDisplay.length === 0) {
    return null;
  }

  const getAllDownloads = (): ReactElement => {
    return (
      <>
        {ordersToDisplay.map((order, index) => {
          const isLastVisible = ordersToDisplay.length === 1;
          return (
            <SingleDownloadItem
              key={order.orderIdKey}
              showBottomPadding={index !== ordersToDisplay.length - 1}
              order={order}
              isOpenedInPrintShop={isOpenedInPrintShop}
              isLastVisible={isLastVisible}
              onHide={(orderIdKey, isLast) => {
                if (order.state === DownloadStateTypes.DOWNLOADING && !order.isFreebieDownloadPageOrder) {
                  void abortOngoingDownload(order.orderId);
                }
                dispatch(removeOrder(orderIdKey));
                if (isLast) {
                  dispatch(setCurrentViewingOrderIdKey(undefined));
                }
              }}
            />
          );
        })}
      </>
    );
  };

  const getUpsellingForModal = (): ReactElement | null => {
    if (!isOpenedInEditor) {
      return null;
    }
    if (!isOpenedInModal) {
      return null;
    }

    if (hasClosedUpsellingInThisSession) {
      return null;
    }

    if (!shouldShowDownloadUpsellingForUser()) {
      return null;
    }

    return (
      <div className={`spacing-m-t-2 w-full h-full`}>
        <DownloadPanelUpselling onClose={onCloseUpselling} key={orderIdKey} isOpenedInModal />
      </div>
    );
  };

  const getPublishingSectionForModal = (): ReactElement | null => {
    if (!isOpenedInEditor) {
      return null;
    }
    if (!isOpenedInModal) {
      return null;
    }
    if (!hasClosedUpsellingInThisSession && shouldShowDownloadUpsellingForUser()) {
      return null;
    }

    return (
      <div className="spacing-m-t-4 w-full h-full">
        <DownloadPanelPublishingSection isOpenedInModal />
      </div>
    );
  };

  const getHeadingForState = (): string => {
    const allComplete = ordersToDisplay.every((order) => order.state === DownloadStateTypes.READY);
    const hasError = ordersToDisplay.some((order) => order.state === DownloadStateTypes.ERROR);

    if (allComplete && ordersToDisplay.length > 0) {
      return window.i18next.t('pmwjs_enjoy_the_download');
    }

    if (hasError) {
      return window.i18next.t('pmwjs_something_went_wrong');
    }

    return window.i18next.t('pmwjs_downloading');
  };

  const getYotpoSection = (): ReactElement | null => {
    const allAreFreebieDownloads = ordersToDisplay.length > 0 && ordersToDisplay.every((order) => order.isFreebieDownloadPageOrder === true);
    if (!allAreFreebieDownloads) {
      return null;
    }
    return <DownloadYotpoSection />;
  };

  const getDownloadingScreen = (): ReactElement => {
    return (
      <div className={`${isOpenedInLoadingPopup ? 'spacing-p-0' : 'spacing-p-4'} flex flex-v-row`}>
        {isOpenedInModal || isOpenedInLoadingPopup ? null : (
          <div className="flex flex-h-row flex-items-center spacing-m-b-4">
            <Icon
              onClick={(): void => {
                if (props.onClickBack) {
                  props.onClickBack();
                }
              }}
              className="spacing-m-r-1"
              icon="icon-arrow-left"
              size={IconSize.SIZE_ICON_16}
              shape={IconShape.SQUARE}
              type={IconType.GHOST}
            />
            <Text className="content-body" size={TextSize.SMALL} bold val={getHeadingForState()} />
          </div>
        )}
        {getAllDownloads()}
        {isOpenedInEditor ? getYotpoSection() : null}
        {getUpsellingForModal()}
        {getPublishingSectionForModal()}
      </div>
    );
  };

  return getDownloadingScreen();
}
